import React, { useState, useEffect, useCallback } from 'react';

const COLORS = {
  bg: '#0a0b0d',
  surface: '#14161a',
  surfaceLight: '#1f2229',
  primary: '#00f2ff',
  secondary: '#7000ff',
  critical: '#ff0055',
  high: '#ff8800',
  medium: '#ffcc00',
  info: '#00ccff',
  success: '#00ff88',
  text: '#e1e1e1',
  textDim: '#808a9d',
  border: '#2a2e38'
};

const Icons = {
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Activity: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  Target: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Logout: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
  ),
  Mail: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
  )
};

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('portier_token'));
  const [view, setView] = useState('dashboard');
  const [targets, setTargets] = useState([]);
  const [scans, setScans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeScanId, setActiveScanId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loginError, setLoginError] = useState('');

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...options.headers, 'Authorization': `Bearer ${token}` };
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) { setToken(null); localStorage.removeItem('portier_token'); throw new Error('Unauthorized'); }
    return res;
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [tRes, sRes, setRes] = await Promise.all([
        authFetch('/api/targets'),
        authFetch('/api/scans'),
        authFetch('/api/settings')
      ]);
      setTargets(await tRes.json());
      setScans(await sRes.json());
      setSettings(await setRes.json());
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [token, authFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!activeScanId || !token) return;
    const interval = setInterval(async () => {
      try {
        const res = await authFetch(`/api/scans/${activeScanId}/status`);
        const status = await res.json();
        setScanStatus(status);
        if (status.status === 'done') { setActiveScanId(null); fetchData(); }
      } catch (err) { setActiveScanId(null); }
    }, 2000);
    return () => clearInterval(interval);
  }, [activeScanId, token, authFetch, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: e.target.username.value, password: e.target.password.value })
      });
      if (!res.ok) throw new Error('Invalid credentials');
      const data = await res.json();
      setToken(data.token);
      localStorage.setItem('portier_token', data.token);
    } catch (err) { setLoginError(err.message); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.enabled = formData.get('enabled') === 'on';
    data.smtp_port = parseInt(data.smtp_port);
    try {
      const res = await authFetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      setSettings(await res.json());
      alert('Settings saved');
    } catch (err) { alert('Save failed'); }
  };

  const testMail = async () => {
    try {
      const res = await authFetch('/api/settings/test-mail', { method: 'POST' });
      if (res.ok) alert('Test mail sent!');
      else alert('Failed to send test mail');
    } catch (err) { alert(err.message); }
  };

  const renderDashboard = () => (
    <div className="view-container">
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Targets</div><div className="stat-value">{targets.length}</div></div>
        <div className="stat-card"><div className="stat-label">Scans</div><div className="stat-value">{scans.length}</div></div>
        <div className="stat-card"><div className="stat-label">Critical</div><div className="stat-value" style={{color: COLORS.critical}}>{scans.reduce((acc, s) => acc + (s.critical || 0), 0)}</div></div>
      </div>
      <div className="section-header"><h2>Recent Scans</h2><button className="btn primary" onClick={() => authFetch('/api/scans', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({target_ids:[]})}).then(r => r.json()).then(d => setActiveScanId(d.scan_id))}>Run Full Scan</button></div>
      <div className="table-wrapper"><table className="data-table">
        <thead><tr><th>Scan ID</th><th>Date</th><th>Status</th><th>Targets</th><th>Alerts</th><th>Actions</th></tr></thead>
        <tbody>{scans.map(s => (
          <tr key={s.id}>
            <td className="mono">{s.id}</td><td>{new Date(s.started_at).toLocaleString()}</td>
            <td><span className={`badge ${s.status}`}>{s.status}</span></td><td>{s.target_count}</td>
            <td>{s.critical > 0 && <span className="tag critical">CRIT: {s.critical}</span>}{s.high > 0 && <span className="tag high">HIGH: {s.high}</span>}{s.critical === 0 && s.high === 0 && <span className="tag success">CLEAN</span>}</td>
            <td><button className="btn-icon" onClick={async () => { const res = await authFetch(`/api/scans/${s.id}`); setSelectedScan(await res.json()); setView('scan-details'); }}>View</button></td>
          </tr>
        ))}</tbody>
      </table></div>
    </div>
  );

  const renderTargets = () => (
    <div className="view-container">
      <div className="section-header"><h2>Manage Targets</h2></div>
      <form className="add-target-form" onSubmit={async (e) => { e.preventDefault(); await authFetch('/api/targets', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({host:e.target.host.value, label:e.target.label.value})}); e.target.reset(); fetchData(); }}>
        <input name="host" placeholder="Hostname or IP" required /><input name="label" placeholder="Friendly Label" /><button type="submit" className="btn primary"><Icons.Plus /> Add</button>
      </form>
      <div className="table-wrapper"><table className="data-table">
        <thead><tr><th>Host</th><th>Label</th><th>Added</th><th>Actions</th></tr></thead>
        <tbody>{targets.map(t => (
          <tr key={t.id}>
            <td className="mono">{t.host}</td><td>{t.label || '-'}</td><td className="text-dim">{new Date(t.created_at).toLocaleDateString()}</td>
            <td>
              <button className="btn-icon" onClick={() => authFetch('/api/scans', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({target_ids:[t.id]})}).then(r => r.json()).then(d => setActiveScanId(d.scan_id))}>Scan</button>
              <button className="btn-icon delete" onClick={async () => { if(window.confirm('Delete?')) { await authFetch(`/api/targets/${t.id}`, {method:'DELETE'}); fetchData(); } }}><Icons.Trash /></button>
            </td>
          </tr>
        ))}</tbody>
      </table></div>
    </div>
  );

  const renderSettings = () => (
    <div className="view-container">
      <div className="section-header"><h2>Global Settings</h2></div>
      <form className="settings-form" onSubmit={saveSettings}>
        <div className="settings-section">
          <h3><Icons.Mail /> Email Notifications</h3>
          <div className="form-grid">
            <div className="input-group"><label>SMTP Server</label><input name="smtp_server" defaultValue={settings?.smtp_server} placeholder="smtp.gmail.com" /></div>
            <div className="input-group"><label>SMTP Port</label><input name="smtp_port" type="number" defaultValue={settings?.smtp_port} /></div>
            <div className="input-group"><label>Username</label><input name="smtp_user" defaultValue={settings?.smtp_user} /></div>
            <div className="input-group"><label>Password</label><input name="smtp_password" type="password" defaultValue={settings?.smtp_password} /></div>
            <div className="input-group"><label>From Address</label><input name="smtp_from" defaultValue={settings?.smtp_from} placeholder="alerts@portier.io" /></div>
            <div className="input-group"><label>To Address</label><input name="smtp_to" defaultValue={settings?.smtp_to} placeholder="admin@example.com" /></div>
          </div>
          <div className="checkbox-group">
            <input type="checkbox" name="enabled" id="enabled" defaultChecked={settings?.enabled} />
            <label htmlFor="enabled">Enable Automatic Email Reports</label>
          </div>
        </div>
        <div className="settings-section">
          <h3><Icons.Activity /> Scheduler</h3>
          <div className="input-group"><label>Scan Schedule (Cron format)</label><input name="schedule_cron" defaultValue={settings?.schedule_cron} placeholder="0 4 * * *" /></div>
          <p className="text-dim" style={{fontSize:12}}>Default: "0 4 * * *" (Every day at 4:00 AM)</p>
        </div>
        <div className="form-actions">
          <button type="button" className="btn secondary" onClick={testMail}>Test Email</button>
          <button type="submit" className="btn primary">Save All Settings</button>
        </div>
      </form>
    </div>
  );

  const renderScanDetails = () => (
    <div className="view-container">
      <div className="section-header"><button className="btn-back" onClick={() => setView('dashboard')}>← Back</button><h2>Results: {selectedScan?.id}</h2></div>
      {selectedScan?.results.map((res, i) => (
        <div key={i} className="result-card">
          <div className="result-header"><h3>{res.host}</h3></div>
          {res.error ? <div className="error-box">{res.error}</div> : (
            <>
              <div className="ports-row">{res.open_ports.map(p => <span key={p} className="port-tag">{p}</span>)}</div>
              {res.findings.map((f, fi) => (
                <div key={fi} className={`finding-item ${f.severity.toLowerCase()}`}>
                  <div className="finding-title"><span className="severity-badge">{f.severity}</span><strong>{f.title}</strong></div>
                  <div className="finding-detail">{f.detail}</div>
                </div>
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );

  if (!token) return (
    <div className="login-screen"><style>{styles}</style><div className="login-card">
      <div className="logo large"><Icons.Shield /><span>PORTIER</span></div>
      <p className="login-subtitle">Secure Access Required</p>
      <form onSubmit={handleLogin}>
        <div className="input-group"><label>Username</label><input name="username" defaultValue="admin" /></div>
        <div className="input-group"><label>Password</label><input name="password" type="password" autoFocus /></div>
        {loginError && <div className="error-msg">{loginError}</div>}
        <button type="submit" className="btn primary full-width">Unlock System</button>
      </form>
    </div></div>
  );

  return (
    <div className="app-shell"><style>{styles}</style>
      <aside className="sidebar">
        <div className="logo"><Icons.Shield /><span>PORTIER</span></div>
        <nav>
          <div className={`nav-link ${view === 'dashboard' ? 'active' : ''}`} onClick={() => setView('dashboard')}><Icons.Activity /> Dashboard</div>
          <div className={`nav-link ${view === 'targets' ? 'active' : ''}`} onClick={() => setView('targets')}><Icons.Target /> Targets</div>
          <div className={`nav-link ${view === 'settings' ? 'active' : ''}`} onClick={() => setView('settings')}><Icons.Settings /> Settings</div>
        </nav>
        <div className="sidebar-footer"><button className="logout-btn" onClick={() => { setToken(null); localStorage.removeItem('portier_token'); }}><Icons.Logout /> Logout</button></div>
      </aside>
      <main className="main-content">
        {activeScanId && scanStatus && (
          <div className="active-scan-banner"><div><strong>Active Scan: {activeScanId}</strong><div style={{fontSize:12,color:COLORS.textDim}}>Processing targets...</div></div><div className="scan-progress-bar"><div className="scan-progress-fill" style={{width:`${(scanStatus.progress/scanStatus.total)*100}%`}}></div></div><div>{scanStatus.progress} / {scanStatus.total}</div></div>
        )}
        {view === 'dashboard' && renderDashboard()}
        {view === 'targets' && renderTargets()}
        {view === 'settings' && renderSettings()}
        {view === 'scan-details' && renderScanDetails()}
      </main>
    </div>
  );
}

const styles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${COLORS.bg}; color: ${COLORS.text}; font-family: system-ui, -apple-system, sans-serif; }
  .app-shell { display: flex; height: 100vh; overflow: hidden; }
  .sidebar { width: 240px; background: ${COLORS.surface}; border-right: 1px solid ${COLORS.border}; display: flex; flex-direction: column; padding: 24px; }
  .logo { display: flex; align-items: center; gap: 12px; font-size: 18px; font-weight: 800; color: ${COLORS.primary}; letter-spacing: 1px; margin-bottom: 48px; }
  .logo.large { flex-direction: column; gap: 16px; font-size: 32px; margin-bottom: 12px; }
  .nav-link { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 8px; color: ${COLORS.textDim}; cursor: pointer; transition: 0.2s; margin-bottom: 4px; }
  .nav-link:hover { background: ${COLORS.surfaceLight}; color: ${COLORS.text}; }
  .nav-link.active { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .sidebar-footer { margin-top: auto; }
  .logout-btn { background: transparent; border: 1px solid ${COLORS.border}; color: ${COLORS.textDim}; width: 100%; padding: 10px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: 0.2s; }
  .logout-btn:hover { border-color: ${COLORS.critical}; color: ${COLORS.critical}; }
  .main-content { flex: 1; overflow-y: auto; padding: 40px; }
  .view-container { max-width: 1000px; margin: 0 auto; }
  .login-screen { height: 100vh; display: flex; align-items: center; justify-content: center; background: ${COLORS.bg}; background-image: radial-gradient(${COLORS.primary}05 1px, transparent 1px); background-size: 40px 40px; }
  .login-card { background: ${COLORS.surface}; padding: 48px; border-radius: 20px; border: 1px solid ${COLORS.border}; width: 400px; box-shadow: 0 20px 50px rgba(0,0,0,0.5); }
  .login-subtitle { text-align: center; color: ${COLORS.textDim}; margin-bottom: 32px; font-size: 14px; }
  .input-group { margin-bottom: 20px; }
  .input-group label { display: block; font-size: 12px; color: ${COLORS.textDim}; margin-bottom: 8px; text-transform: uppercase; }
  .input-group input { width: 100%; background: ${COLORS.bg}; border: 1px solid ${COLORS.border}; padding: 12px; border-radius: 8px; color: ${COLORS.text}; font-size: 16px; transition: 0.2s; }
  .input-group input:focus { border-color: ${COLORS.primary}; outline: none; box-shadow: 0 0 0 4px ${COLORS.primary}10; }
  .error-msg { color: ${COLORS.critical}; font-size: 13px; margin-bottom: 16px; text-align: center; }
  .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 40px; }
  .stat-card { background: ${COLORS.surface}; padding: 24px; border-radius: 16px; border: 1px solid ${COLORS.border}; }
  .stat-label { font-size: 12px; color: ${COLORS.textDim}; text-transform: uppercase; margin-bottom: 8px; }
  .stat-value { font-size: 32px; font-weight: 800; font-family: monospace; }
  .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
  .table-wrapper { background: ${COLORS.surface}; border-radius: 12px; border: 1px solid ${COLORS.border}; overflow: hidden; }
  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th { padding: 16px; background: ${COLORS.surfaceLight}; font-size: 12px; color: ${COLORS.textDim}; text-transform: uppercase; text-align: left; }
  .data-table td { padding: 16px; border-bottom: 1px solid ${COLORS.border}; font-size: 14px; }
  .mono { font-family: monospace; color: ${COLORS.primary}; }
  .btn { padding: 10px 20px; border-radius: 8px; font-weight: 700; cursor: pointer; transition: 0.2s; border: none; display: flex; align-items: center; gap: 8px; }
  .btn.primary { background: ${COLORS.primary}; color: ${COLORS.bg}; }
  .btn.primary:hover { transform: translateY(-1px); box-shadow: 0 8px 20px ${COLORS.primary}40; }
  .btn.secondary { background: ${COLORS.surfaceLight}; color: ${COLORS.text}; border: 1px solid ${COLORS.border}; }
  .btn-icon { background: transparent; border: 1px solid ${COLORS.border}; color: ${COLORS.textDim}; padding: 6px 12px; border-radius: 6px; cursor: pointer; margin-right: 8px; }
  .btn-icon:hover { color: ${COLORS.text}; }
  .btn-icon.delete:hover { color: ${COLORS.critical}; border-color: ${COLORS.critical}; }
  .btn-back { background: transparent; border: none; color: ${COLORS.textDim}; cursor: pointer; margin-bottom: 12px; }
  .badge { padding: 4px 8px; border-radius: 6px; font-size: 11px; font-weight: 800; text-transform: uppercase; }
  .badge.done { background: ${COLORS.success}20; color: ${COLORS.success}; }
  .badge.running { background: ${COLORS.info}20; color: ${COLORS.info}; animation: pulse 2s infinite; }
  @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
  .tag { padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 800; margin-right: 6px; }
  .tag.critical { background: ${COLORS.critical}30; color: ${COLORS.critical}; }
  .tag.high { background: ${COLORS.high}30; color: ${COLORS.high}; }
  .tag.success { background: ${COLORS.success}30; color: ${COLORS.success}; }
  .result-card { background: ${COLORS.surface}; padding: 24px; border-radius: 16px; margin-bottom: 24px; border: 1px solid ${COLORS.border}; }
  .port-tag { display: inline-block; padding: 4px 8px; background: ${COLORS.bg}; border: 1px solid ${COLORS.border}; border-radius: 6px; margin: 4px; font-family: monospace; color: ${COLORS.primary}; }
  .finding-item { padding: 16px; border-radius: 8px; margin-top: 12px; }
  .finding-item.critical { background: ${COLORS.critical}10; border-left: 4px solid ${COLORS.critical}; }
  .finding-item.high { background: ${COLORS.high}10; border-left: 4px solid ${COLORS.high}; }
  .finding-item.medium { background: ${COLORS.medium}10; border-left: 4px solid ${COLORS.medium}; }
  .finding-item.info { background: ${COLORS.info}10; border-left: 4px solid ${COLORS.info}; }
  .finding-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
  .severity-badge { font-size: 10px; font-weight: 900; padding: 2px 6px; border-radius: 4px; text-transform: uppercase; background: rgba(255,255,255,0.1); }
  .critical .severity-badge { background: ${COLORS.critical}; color: white; }
  .high .severity-badge { background: ${COLORS.high}; color: white; }
  .medium .severity-badge { background: ${COLORS.medium}; color: black; }
  .info .severity-badge { background: ${COLORS.info}; color: black; }
  .finding-detail { font-family: monospace; font-size: 13px; color: ${COLORS.textDim}; white-space: pre-wrap; line-height: 1.4; }
  .add-target-form { display: flex; gap: 12px; margin-bottom: 30px; }
  .add-target-form input { flex: 1; background: ${COLORS.surface}; border: 1px solid ${COLORS.border}; padding: 12px; border-radius: 8px; color: ${COLORS.text}; }
  .active-scan-banner { background: ${COLORS.primary}10; border: 1px solid ${COLORS.primary}30; padding: 16px; border-radius: 12px; margin-bottom: 30px; display: flex; align-items: center; justify-content: space-between; }
  .scan-progress-bar { flex: 1; height: 8px; background: ${COLORS.border}; border-radius: 4px; margin: 0 20px; overflow: hidden; }
  .scan-progress-fill { height: 100%; background: ${COLORS.primary}; transition: width 0.3s; }
  .settings-form { max-width: 800px; }
  .settings-section { background: ${COLORS.surface}; border: 1px solid ${COLORS.border}; padding: 24px; border-radius: 16px; margin-bottom: 24px; }
  .settings-section h3 { margin-bottom: 20px; display: flex; align-items: center; gap: 12px; color: ${COLORS.primary}; }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .checkbox-group { display: flex; align-items: center; gap: 12px; margin-top: 20px; }
  .checkbox-group input { width: 18px; height: 18px; }
  .form-actions { display: flex; justify-content: space-between; align-items: center; }
`;
